import * as React from 'react';

import { captureException } from '@sentry/browser';
import {
  ActivityStage,
  ActivityType,
  QuestionTypes,
} from '@showbie-socrative/socrative-utils';
import { useQueryClient } from '@tanstack/react-query';
import { translate } from '~l10n/client';
import produce from 'immer';
import { omit } from 'underscore';

import { ActivityResponse } from '~teacher/hook';

import { legacyHideResponse } from '../../../api/activities/activityApi';
import { useResultQuestionState } from '../ActivityResult/ActivityResultQuestionProvider';
import { ResultsQuestionAnswersMC } from './ResultsQuestionAnswersMC';
import { ResultsQuestionAnswersSA } from './ResultsQuestionAnswersSA';
import { ResultsQuestionAnswersTF } from './ResultsQuestionAnswersTF';

const hideResponseUpdater = ({ responseId }: { responseId: number }) =>
  produce((activityData: ActivityResponse) => {
    activityData.responses = omit(activityData.responses, String(responseId));
    return activityData;
  });

export function CorrectAnswerIndicator(): React.ReactElement {
  return (
    <svg
      width="27"
      height="27"
      viewBox="0 0 27 27"
      role="img"
      aria-labelledby="correct-title"
    >
      <title id="correct-title">{translate('Correct')}</title>
      <circle
        className="fill-green-500"
        fillRule="evenodd"
        cx="13.5"
        cy="13.5"
        r="13.5"
      />
      <path
        className="stroke-white"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="3"
        d="M8 13.5l3.5 3.5 7-7"
      />
    </svg>
  );
}

interface Props {
  showResults: boolean;
  showAnswers: boolean;
  showNames: boolean;
  openErrorModal: () => void;
}

export function ResultsQuestionAnswers({
  showResults,
  showAnswers,
  showNames,
  openErrorModal,
}: Props): React.ReactElement {
  const questionContext = useResultQuestionState();
  const {
    answers,
    question = {
      hasCorrectAnswer: undefined,
      questionId: undefined,
      type: undefined,
    },
    participants,
    responses,
    activityType,
    activityStage,
    activityInstanceId,
    roomName,
    numParticipantsResponded,
  } = questionContext;
  const queryClient = useQueryClient();
  const hideResponseInQueryData = (responseId: number) => {
    queryClient.setQueryData(
      ['activity', activityInstanceId],
      hideResponseUpdater({ responseId })
    );
  };
  let onRemoveResponse = hideResponseInQueryData;

  if (activityType === ActivityType.QuickQuestionShortAnswer) {
    onRemoveResponse = (responseId) => {
      legacyHideResponse(responseId, roomName)
        .then(() => {
          hideResponseInQueryData(responseId);
        })
        .catch((err) => {
          console.error(err);
          captureException(err);
          openErrorModal();
        });
    };
  }

  switch (question.type) {
    case QuestionTypes.TrueFalse:
      return (
        <ResultsQuestionAnswersTF
          answers={answers}
          showResults={activityType === ActivityType.NoReport || showResults}
          responses={responses}
          hasCorrectAnswer={question.hasCorrectAnswer}
        />
      );
    case QuestionTypes.FreeResponse:
      return (
        <ResultsQuestionAnswersSA
          participants={participants}
          questionId={question.questionId}
          numParticipantsResponded={numParticipantsResponded}
          showAnswers={showAnswers}
          showNames={showNames}
          onRemoveResponse={onRemoveResponse}
          showRemoveAnswer={
            ActivityStage.InProgress === activityStage &&
            ActivityType.QuickQuestionShortAnswer === activityType
          }
        />
      );
    case QuestionTypes.MultipleChoice:
      return (
        <ResultsQuestionAnswersMC
          answers={answers}
          responses={responses}
          hasCorrectAnswer={question.hasCorrectAnswer}
          showResults={
            [ActivityType.NoReport, ActivityType.Vote].includes(activityType) ||
            showResults
          }
        />
      );
    default:
      console.error('Unhandled Question Type');
      return null;
  }
}
